import { Component, OnChanges, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef } from '@angular/core';
import {  ContainerPositionDto, ContainerStatus, CurrentAssetLocationDto, CustomerLocationDto, LocationServiceDto} from 'src/shared/service-proxies/service-proxies';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment';
import { DateTimeHelper } from 'src/app/helpers/datetime'
import { Router } from '@angular/router';
import * as L from 'leaflet';
import { GeoHelper } from 'src/app/helpers/geo';
import { LocationClassNameHelper } from 'src/app/enums/LocationTypeNames';

export class ContainerDropDownListItem {
  constructor(id?: string, name?: string) {
    this.id = id,
    this.name = name;
  }

  id: string;
  name: string;
}

@Component({
    selector: 'app-widget-containers',
    templateUrl: './containers.component.html',
    styleUrls: ['./containers.component.scss'],
    standalone: false
})
export class ContainersWidgetComponent implements OnChanges, OnInit {
  @ViewChild('mapdiv') 
  mapDiv: ElementRef;

  // Inputs
  @Input()
  containers: ContainerPositionDto[]

  @Input()
  location: CustomerLocationDto;

  @Output()
  onExport: EventEmitter<any> = new EventEmitter();

  private isOnExportUsed = false;

  // Private variables
  containerList: ContainerDropDownListItem[] = [];
  typeList: ContainerDropDownListItem[] = [];
  statusList: ContainerDropDownListItem[] = [];
  selectedContainerId: string;
  selectedContainerTypeId: string;
  selectedStatusId: string;
  filteredAssetsOnSite: ContainerPositionDto[] = [];
  selectedImage: string | null = null;
  showAssetId: string | null = null;
  selectedContainers: ContainerPositionDto[] | null = null;
  isPopupOpen = false;
  zoom: number = 0;
  isFirstTime: boolean = true;

  // Public variables
  containersPage: ContainerPositionDto[];

  private map;
  private mapLayer = L.tileLayer('http://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&key=AIzaSyChxd9LP-pHKK1qe1nPlECcK5iJb-a4hg8', {
      maxZoom: 18,
      minZoom: 3,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  });
  private drawLayer = L.layerGroup();
  private defaultImageURL: string = 'https://pinwebsiteimages.blob.core.windows.net/binreportimages/10000_OPN_image_front.jpg';

  constructor(private router: Router) { }

  ngOnChanges(changes) {
    this.updateView();
  }

  ngOnInit(): void {
    this.isOnExportUsed = this.onExport.observers.length > 0;
  }

  ngAfterViewInit() {
    this.initMap();
  }

  ngOnDestroy(): void {
    if (this.map ){
      this.map.eachLayer(function(layer){
          layer.remove();
      });
      this.map.remove();
      this.map = null;
      this.mapDiv.nativeElement.innerHTML = "";
    }
  }

  updateView() {
    let page: ContainerPositionDto[] = [];

    this.addContainerLists(this.containers);
    this.containers.forEach(container => {
        page.push(container);
    });

    this.filterClientSide(this.containers)
  }

  filterClientSide(assetsOnSite: ContainerPositionDto[]) {    
    if (assetsOnSite) {
      this.containersPage = assetsOnSite;
    }

    let filteredAssetsOnSite = this.containersPage;

    if(this.selectedContainerId) {
      filteredAssetsOnSite = filteredAssetsOnSite.filter(a => a.containerName == this.selectedContainerId);
    }
    if(this.selectedContainerTypeId) {
      filteredAssetsOnSite = filteredAssetsOnSite.filter(a => a.containerType == this.selectedContainerTypeId);
    }

    if(this.selectedStatusId) {
      console.log(this.selectedStatusId);
      console.log(filteredAssetsOnSite);
      filteredAssetsOnSite = filteredAssetsOnSite.filter(a => a.statusName == this.selectedStatusId);
    }

    this.filteredAssetsOnSite = filteredAssetsOnSite;
    this.initMap();
  }

    private initMap(): void {
      if (!this.location || !this.filteredAssetsOnSite || !this.mapDiv) {
        return;
      }
      
      let containers = this.filteredAssetsOnSite.filter(p => p.position);
  
      if (!this.map) {
        let zoom = this.zoom;

        if (!zoom) {
          zoom = 20;
  
          if (containers.length > 0) {
            zoom = -1 + GeoHelper.CalculateZoom(containers.map(p => ({ lat: p.position.latitude, lon: p.position.longitude })), this.mapDiv.nativeElement.offsetHeight, this.mapDiv.nativeElement.offsetWidth);
          }
        }

        this.map = L.map('map', {
          center: [ this.location.latitude, this.location.longitude ],
          zoom: zoom,
          scrollWheelZoom: false
        });
  
        this.zoom = zoom;
        this.mapLayer.addTo(this.map);
        this.drawLayer.addTo(this.map);
        setTimeout(() => {
          this.map.invalidateSize(true);
        }, 1)
      } else {
        this.map.flyTo(new L.LatLng(this.location.latitude, this.location.longitude), this.zoom, {
          animate: true,
          duration: this.isFirstTime ? 1 : 0
        });
      }
  
      this.isFirstTime = false;

      // Clear and re add draw layer
      this.map.removeLayer(this.drawLayer);
      this.drawLayer = L.layerGroup();
      this.drawLayer.addTo(this.map);
  
      this.addLocationPin(this.location.locationType, this.location.latitude, this.location.longitude, containers.filter(c => !c.isKnown));
      this.addContainerPins(containers.filter(c => c.isKnown));
    }
  
    addLocationPin(locationTypeName, locationLatitude, locationLongitude, containers: ContainerPositionDto[]) {
      let className = LocationClassNameHelper.getLocationClassName(locationTypeName);
      let title = containers.map(c => this.getContainerLine(c)).join("\n");
      let icon = L.divIcon({
        className: 'custom-div-icon',
        html: "<div class='marker-pin " + className + "'></div><i class='material-icons' title='" + title + "'>home</i>",
        iconSize: [30, 42],
        iconAnchor: [15, 36]
      }); 
      
      const marker = L.marker([locationLatitude, locationLongitude], { icon: icon }).addTo(this.drawLayer);

      if (containers.length) {
        marker.on('click', () => {
          const imageUrl = !containers[0].assetURL ? this.defaultImageURL : containers[0].assetURL;
          this.openPopup(imageUrl, containers[0].containerName, containers);
        });
      }
    }
  
    addContainerPins(containers: ContainerPositionDto[]) {
      let className = "depot";
      let markers = [];
  
      //this.data.pinsTotal = 0;
  
      containers.forEach(c => {     
        let marker = markers.find(m => m.latitude == c.position.latitude && m.longitude == c.position.longitude);
  
        if (marker != null) {
          marker.title += `\n\n${this.getContainerTitle(c)}`;
          marker.containers.push(c);
        }
        else {
          markers.push({
            latitude: c.position.latitude,
            longitude: c.position.longitude,
            title: this.getContainerTitle(c),
            assetId: c.containerName,
            assetURL: c.assetURL,
            assetTypeClassification: c.assetTypeClassification,
            containers: [c]
          });
  
          //this.data.pinsTotal++;
        };
      });
  
      markers.forEach(m => {
        let imagePath = m.assetTypeClassification == 0
          ? '/assets/img/RoRo.png'
          : '/assets/img/Skip.png';
        let icon = L.divIcon({
          className: 'custom-div-icon',
          html: "<div class='marker-pin " + className + `'></div><i class='material-icons' title='${m.title}'>crop_square</i>`,
          iconSize: [30, 42],
          iconAnchor: [15, 36]
        });

        const marker = L.marker([m.latitude, m.longitude], { icon: icon }).addTo(this.drawLayer);

        marker.on('click', () => {
          const imageUrl = m.assetURL ?? this.defaultImageURL;
          this.openPopup(imageUrl, m.assetId, m.containers);
        });
      });
    }
  
    getContainerLine(container: ContainerPositionDto): any {
      return `ID: ${container.containerName}, Type: ${container.containerType}, On Site: ${container.daysOnSite} day(s)${(container.externalReference ? `, Contract: ${container.externalReference}`: '')}${(container.wasteTypeName ? `, Waste: ${container.wasteTypeName}`: '')}`;
    }
  
    getContainerTitle(container: ContainerPositionDto): any {
      return `ID: ${container.containerName}\nType: ${container.containerType}\nOn Site: ${container.daysOnSite} day(s)${(container.externalReference ? `\nContract: ${container.externalReference}`: '')}${(container.wasteTypeName ? `\nWaste: ${container.wasteTypeName}`: '')}`;
    }

  containerChange(newValue) {
    if (newValue) {
      this.selectedContainerId = newValue;
    } else {
      this.selectedContainerId = null;
    }

    this.filterClientSide(null);
  }

  typeChange(newValue) {
    if (newValue) {
      this.selectedContainerTypeId = newValue;
    } else {
      this.selectedContainerTypeId = null;
    }    

    this.filterClientSide(null);
  }

  statusChange(newValue) {
    if (newValue) {
      this.selectedStatusId = newValue;
    } else {
      this.selectedStatusId = null;
    }
    console.log(newValue)

    this.filterClientSide(null);
  }

  addContainerLists(assets: ContainerPositionDto[]) {
    var containerList: ContainerDropDownListItem[] = []
    var assetTypeList: ContainerDropDownListItem[] = []
    var statusList: ContainerDropDownListItem[] = []

    assets.forEach(asset => {
      let containerItem = new ContainerDropDownListItem()
      containerItem.id = asset.containerName;
      containerItem.name = asset.containerName;
      containerList.push(containerItem)

      if (!assetTypeList.find(a => a.id == asset.containerType)) {
        let typeItem = new ContainerDropDownListItem()
        typeItem.id = asset.containerType;
        typeItem.name = asset.containerType;
        assetTypeList.push(typeItem)
      }

      if (!statusList.find(a => a.id == asset.statusName)) {
        let statusItem = new ContainerDropDownListItem()

        statusItem.id = asset.statusName;
        statusItem.name = asset.statusDisplay;
        statusList.push(statusItem)
      }
    });

    containerList = containerList.sort(function(a, b){
      if(a.name < b.name) { return -1; }
      if(a.name > b.name) { return 1; }
      return 0;
    })

    assetTypeList = assetTypeList.sort(function(a, b){
      if(a.name < b.name) { return -1; }
      if(a.name > b.name) { return 1; }
      return 0;
    })

    statusList = statusList.sort(function(a, b){
      if(a.name < b.name) { return -1; }
      if(a.name > b.name) { return 1; }
      return 0;
    })

    this.containerList = containerList;
    this.typeList = assetTypeList;
    this.statusList = statusList;
  }

  openPopup(imageSrc: string, selectedAsset: string, containers: ContainerPositionDto[]): void {
    this.selectedImage = imageSrc;
    this.showAssetId = selectedAsset;
    this.selectedContainers = containers;
    this.isPopupOpen = true;
  }

  closePopup(): void {
    this.isPopupOpen = false;
  }
}
